import { useState, useCallback } from 'react';

import { Eye, EyeOff } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { ResourcePicker } from './resource-picker';

import { cn } from '@/lib/utils';

import { UI_FIELD_TYPES } from '@optidev/shared-types';
import type { UIField, SelectedResource } from '@optidev/shared-types';

interface FieldRendererProps {
  field: UIField;
  value: unknown;
  maskedValue?: unknown;  // For showing current value as placeholder in edit mode
  onChange: (value: unknown) => void;
  error?: string;
  // For resource_picker type
  accessToken?: string;
  connectionId?: string;  // For Microsoft picker to fetch SharePoint token
  getAuthToken?: () => Promise<string | null>;  // For authenticated API calls
  // Suppresses the inline helper line when the form collects descriptions into one
  // "Where do I find these?" disclosure instead
  hideDescription?: boolean;
}

/**
 * FieldRenderer - Renders form field based on UIField type
 *
 * Supports:
 * - text: Standard text input
 * - password: Password input with show/hide toggle
 * - textarea: Multi-line text area
 * - select: Dropdown with options
 * - json: Textarea with JSON validation
 * - number: Numeric input
 * - checkbox: Boolean checkbox
 * - upload: File upload (renders as textarea for JSON files)
 */
export function FieldRenderer({ field, value, maskedValue, onChange, error, accessToken, connectionId, getAuthToken, hideDescription = false }: FieldRendererProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const fieldId = `field-${field.name}`;

  // Validate JSON input
  const validateJson = useCallback((input: string): boolean => {
    if (!input || input.trim() === '') {
      setJsonError(null);
      return true;
    }
    try {
      JSON.parse(input);
      setJsonError(null);
      return true;
    } catch {
      setJsonError('Invalid JSON format');
      return false;
    }
  }, []);

  // Handle JSON field change
  const handleJsonChange = useCallback(
    (input: string) => {
      onChange(input);
      validateJson(input);
    },
    [onChange, validateJson]
  );

  // Render the appropriate input based on field type
  const renderInput = (): React.ReactNode => {
    switch (field.type) {
      case UI_FIELD_TYPES.TEXT:
        return (
          <Input
            id={fieldId}
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={maskedValue ? `Current: ${maskedValue}` : field.placeholder}
            required={field.required}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            pattern={field.validation?.pattern}
            className={cn(error && 'border-destructive')}
          />
        );

      case UI_FIELD_TYPES.PASSWORD:
        return (
          <div className="relative">
            <Input
              id={fieldId}
              type={showPassword ? 'text' : 'password'}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={maskedValue ? `Current: ${maskedValue}` : field.placeholder}
              required={field.required}
              minLength={field.validation?.minLength}
              maxLength={field.validation?.maxLength}
              className={cn('pr-10', error && 'border-destructive')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        );

      case UI_FIELD_TYPES.TEXTAREA:
        return (
          <Textarea
            id={fieldId}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={maskedValue ? `Current: ${maskedValue}` : field.placeholder}
            required={field.required}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            className={cn('min-h-[100px]', error && 'border-destructive')}
          />
        );

      case UI_FIELD_TYPES.JSON:
      case UI_FIELD_TYPES.UPLOAD:
        return (
          <div className="space-y-1">
            <Textarea
              id={fieldId}
              value={(value as string) ?? ''}
              onChange={(e) => handleJsonChange(e.target.value)}
              placeholder={maskedValue ? `Current: ${maskedValue}` : (field.placeholder ?? 'Paste JSON here...')}
              required={field.required}
              className={cn(
                'min-h-[150px] text-sm',
                (error || jsonError) && 'border-destructive'
              )}
            />
            {jsonError && (
              <p className="text-xs text-destructive">{jsonError}</p>
            )}
          </div>
        );

      case UI_FIELD_TYPES.SELECT:
        return (
          <Select
            value={(value as string) ?? ''}
            onValueChange={onChange}
          >
            <SelectTrigger
              id={fieldId}
              className={cn(error && 'border-destructive')}
            >
              <SelectValue placeholder={field.placeholder ?? 'Select an option...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option: { value: string; label: string }) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case UI_FIELD_TYPES.NUMBER:
        return (
          <Input
            id={fieldId}
            type="number"
            value={(value as number) ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={field.placeholder}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            className={cn(error && 'border-destructive')}
          />
        );

      case UI_FIELD_TYPES.CHECKBOX:
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={fieldId}
              checked={(value as boolean) ?? false}
              onCheckedChange={(checked) => onChange(checked)}
            />
            <Label htmlFor={fieldId} className="text-sm font-normal cursor-pointer">
              {field.description ?? field.label}
            </Label>
          </div>
        );

      case UI_FIELD_TYPES.RESOURCE_PICKER:
        if (!field.pickerConfig) {
          return <p className="text-sm text-destructive">Missing picker configuration</p>;
        }
        return (
          <ResourcePicker
            config={field.pickerConfig}
            value={(value as SelectedResource[]) ?? []}
            onChange={(resources) => onChange(resources)}
            accessToken={accessToken}
            connectionId={connectionId}
            getAuthToken={getAuthToken}
            disabled={!accessToken && !connectionId}
            error={error}
            label={field.label}
            required={field.required}
            description={field.description}
          />
        );

      default:
        return (
          <Input
            id={fieldId}
            type="text"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={maskedValue ? `Current: ${maskedValue}` : field.placeholder}
            required={field.required}
            className={cn(error && 'border-destructive')}
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      {/* Label - skip for checkbox and resource_picker since they have inline labels */}
      {field.type !== UI_FIELD_TYPES.CHECKBOX && field.type !== UI_FIELD_TYPES.RESOURCE_PICKER && (
        <Label htmlFor={fieldId} className="flex items-center gap-1">
          {field.label}
          {field.required && <span className="text-destructive">*</span>}
        </Label>
      )}

      {/* Input */}
      {renderInput()}

      {/* Description - skip for checkbox and resource_picker since they handle descriptions */}
      {field.description && !hideDescription && field.type !== UI_FIELD_TYPES.CHECKBOX && field.type !== UI_FIELD_TYPES.RESOURCE_PICKER && (
        <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">{field.description}</p>
      )}

      {/* Error message - skip for resource_picker since it handles errors */}
      {error && !jsonError && field.type !== UI_FIELD_TYPES.RESOURCE_PICKER && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}

export default FieldRenderer;
